import React, { useState } from 'react';
import { FileText, Sparkles, Target, Users, Calendar, ArrowLeft } from 'lucide-react';
import { Button } from '../ui/Button';
import { ArticlesList } from './ArticlesList';
import { ArticleViewer } from './ArticleViewer';
import { ArticleTypeSelector } from './ArticleTypeSelector';
import type { ArticleTypeId } from '../../types';

interface BriefingSelectorProps {
  onSelectMode: (mode: 'chat' | 'form') => void;
  onOpenScheduler: () => void;
  projectId?: string;
  // Props para tipo de artículo
  articleType: ArticleTypeId;
  onArticleTypeChange: (type: ArticleTypeId) => void;
  comparisonUrls: string[];
  onComparisonUrlsChange: (urls: string[]) => void;
  mainSourceUrl: string;
  onMainSourceUrlChange: (url: string) => void;
}

export const BriefingSelector: React.FC<BriefingSelectorProps> = ({
  onSelectMode,
  onOpenScheduler,
  projectId,
  articleType,
  onArticleTypeChange,
  comparisonUrls,
  onComparisonUrlsChange,
  mainSourceUrl,
  onMainSourceUrlChange
}) => {
  const [showArticlesList, setShowArticlesList] = useState(false);
  const [selectedArticleToView, setSelectedArticleToView] = useState<any>(null);
  const [showTypeSelector, setShowTypeSelector] = useState(true);

  // Si estamos mostrando el selector de tipo
  if (showTypeSelector) {
    return (
      <div className="min-h-[600px] flex items-center justify-center p-8">
        <div className="max-w-4xl w-full">
          {/* Header */}
          <div className="text-center mb-8 animate-fade-in">
            <div className="flex items-center justify-center mb-5">
              <div className="w-14 h-14 rounded-2xl bg-gray-900 flex items-center justify-center">
                <Sparkles className="w-7 h-7 text-white" />
              </div>
            </div>
            <h1 className="text-3xl font-semibold text-gray-900 tracking-tight mb-2">
              Generador de Contenido con IA
            </h1>
            <p className="text-base text-gray-500 max-w-xl mx-auto">
              Paso 1: Selecciona el tipo de artículo que quieres crear
            </p>
          </div>

          <ArticleTypeSelector
            selectedType={articleType}
            onSelectType={onArticleTypeChange}
            onContinue={() => setShowTypeSelector(false)}
            comparisonUrls={comparisonUrls}
            onComparisonUrlsChange={onComparisonUrlsChange}
            mainSourceUrl={mainSourceUrl}
            onMainSourceUrlChange={onMainSourceUrlChange}
          />
        </div>

        {/* Modal de Lista de Artículos */}
        {showArticlesList && projectId && (
          <div className="fixed inset-0 bg-black/40 backdrop-blur-sm flex items-center justify-center z-50 p-4">
            <div className="w-full max-w-4xl max-h-[90vh] overflow-y-auto bg-white rounded-2xl shadow-2xl">
              <div className="sticky top-0 bg-white border-b border-gray-100 p-5 flex items-center justify-between">
                <h2 className="text-lg font-semibold text-gray-900">Artículos Generados de este Proyecto</h2>
                <button
                  onClick={() => setShowArticlesList(false)}
                  className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
                >
                  <FileText className="w-5 h-5 text-gray-400" />
                </button>
              </div>
              <div className="p-5">
                <ArticlesList
                  projectId={projectId}
                  onViewArticle={(article) => {
                    setSelectedArticleToView(article);
                    setShowArticlesList(false);
                  }}
                />
              </div>
            </div>
          </div>
        )}

        {/* Modal de Visor de Artículo */}
        {selectedArticleToView && (
          <ArticleViewer
            article={selectedArticleToView}
            projectId={projectId}
            onClose={() => setSelectedArticleToView(null)}
            onBack={() => {
              setSelectedArticleToView(null);
              setShowArticlesList(true);
            }}
          />
        )}
      </div>
    );
  }

  // Mostrar selector de modo (después de elegir tipo)
  return (
    <div className="min-h-[600px] flex items-center justify-center p-8">
      <div className="max-w-5xl w-full">
        {/* Back Button */}
        <div className="mb-6 animate-fade-in">
          <Button
            variant="ghost"
            onClick={() => setShowTypeSelector(true)}
            icon={<ArrowLeft className="w-4 h-4" />}
            className="text-gray-500 hover:text-gray-900"
          >
            Cambiar tipo de artículo
          </Button>
        </div>

        {/* Header */}
        <div className="text-center mb-10 animate-fade-in">
          <div className="flex items-center justify-center mb-5">
            <div className="w-14 h-14 rounded-2xl bg-gray-900 flex items-center justify-center">
              <Sparkles className="w-7 h-7 text-white" />
            </div>
          </div>
          <h1 className="text-3xl font-semibold text-gray-900 tracking-tight mb-2">
            Generador de Contenido con IA
          </h1>
          <p className="text-base text-gray-500 max-w-xl mx-auto">
            Paso 2: Elige cómo quieres comenzar a crear tu contenido
          </p>
          {/* Badge con tipo seleccionado */}
          <div className="mt-4 inline-flex items-center gap-2 px-4 py-2 bg-gray-100 text-gray-700 rounded-full text-sm font-medium">
            <Sparkles className="w-4 h-4" />
            Tipo: {articleType === 'wikipedia' ? 'Enciclopédico' : articleType === 'comparative' ? 'Comparativa' : articleType === 'standard' ? 'Explicativo' : 'Noticia'}
          </div>
        </div>

        {/* Options - Minimalist Cards */}
        <div className="grid md:grid-cols-2 gap-5 animate-slide-up">
          {/* Scheduler Option */}
          <button
            onClick={onOpenScheduler}
            className="group text-left bg-white border border-gray-200 rounded-2xl p-6 transition-all duration-200 hover:border-gray-300 hover:shadow-lg"
          >
            <div className="flex items-start gap-4">
              <div className="w-12 h-12 rounded-xl bg-gray-100 flex items-center justify-center flex-shrink-0 group-hover:bg-gray-900 group-hover:text-white transition-colors">
                <Calendar className="w-6 h-6 text-gray-600 group-hover:text-white" />
              </div>
              <div className="flex-1">
                <h3 className="text-lg font-semibold text-gray-900 mb-1">Programar Contenido</h3>
                <p className="text-sm text-gray-500 mb-4">
                  Organiza y planifica tus publicaciones en un calendario interactivo
                </p>
                <div className="space-y-2">
                  <div className="flex items-center gap-2 text-sm text-gray-600">
                    <Calendar className="w-4 h-4 text-gray-400" />
                    <span>Calendario visual</span>
                  </div>
                  <div className="flex items-center gap-2 text-sm text-gray-600">
                    <Users className="w-4 h-4 text-gray-400" />
                    <span>Gestión de equipo</span>
                  </div>
                  <div className="flex items-center gap-2 text-sm text-gray-600">
                    <Target className="w-4 h-4 text-gray-400" />
                    <span>Objetivos mensuales</span>
                  </div>
                </div>
              </div>
            </div>
            <div className="mt-5 pt-4 border-t border-gray-100">
              <span className="text-sm font-medium text-gray-900 group-hover:text-gray-700">
                Abrir Calendario →
              </span>
            </div>
          </button>

          {/* Form Option */}
          <button
            onClick={() => onSelectMode('form')}
            className="group text-left bg-white border border-gray-200 rounded-2xl p-6 transition-all duration-200 hover:border-gray-300 hover:shadow-lg"
          >
            <div className="flex items-start gap-4">
              <div className="w-12 h-12 rounded-xl bg-gray-100 flex items-center justify-center flex-shrink-0 group-hover:bg-gray-900 group-hover:text-white transition-colors">
                <FileText className="w-6 h-6 text-gray-600 group-hover:text-white" />
              </div>
              <div className="flex-1">
                <h3 className="text-lg font-semibold text-gray-900 mb-1">Formulario Tradicional</h3>
                <p className="text-sm text-gray-500 mb-4">
                  Control total sobre cada aspecto de tu contenido con opciones detalladas
                </p>
                <div className="space-y-2">
                  <div className="flex items-center gap-2 text-sm text-gray-600">
                    <Target className="w-4 h-4 text-gray-400" />
                    <span>Control total y preciso</span>
                  </div>
                  <div className="flex items-center gap-2 text-sm text-gray-600">
                    <Users className="w-4 h-4 text-gray-400" />
                    <span>Para usuarios avanzados</span>
                  </div>
                  <div className="flex items-center gap-2 text-sm text-gray-600">
                    <Sparkles className="w-4 h-4 text-gray-400" />
                    <span>Configuración detallada</span>
                  </div>
                </div>
              </div>
            </div>
            <div className="mt-5 pt-4 border-t border-gray-100">
              <span className="text-sm font-medium text-gray-900 group-hover:text-gray-700">
                Abrir Formulario →
              </span>
            </div>
          </button>
        </div>

        {/* Footer hint */}
        <div className="text-center mt-10 text-gray-400 text-sm animate-fade-in">
          <p>Ambas opciones generan contenido de la misma calidad. Elige la que mejor se adapte a tu estilo.</p>
        </div>
      </div>

      {/* Modal de Lista de Artículos */}
      {showArticlesList && projectId && (
        <div className="fixed inset-0 bg-black/40 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="w-full max-w-4xl max-h-[90vh] overflow-y-auto bg-white rounded-2xl shadow-2xl">
            <div className="sticky top-0 bg-white border-b border-gray-100 p-5 flex items-center justify-between">
              <h2 className="text-lg font-semibold text-gray-900">Artículos Generados de este Proyecto</h2>
              <button
                onClick={() => setShowArticlesList(false)}
                className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
              >
                <FileText className="w-5 h-5 text-gray-400" />
              </button>
            </div>
            <div className="p-5">
              <ArticlesList
                projectId={projectId}
                onViewArticle={(article) => {
                  setSelectedArticleToView(article);
                  setShowArticlesList(false);
                }}
              />
            </div>
          </div>
        </div>
      )}

      {/* Modal de Visor de Artículo */}
      {selectedArticleToView && (
        <ArticleViewer
          article={selectedArticleToView}
          projectId={projectId}
          onClose={() => setSelectedArticleToView(null)}
          onBack={() => {
            setSelectedArticleToView(null);
            setShowArticlesList(true);
          }}
        />
      )}
    </div>
  );
};
